import { useEffect, type ReactNode } from 'react';

interface PageLayoutProps {
  children: ReactNode;
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}

export default function PageLayout({ children, title, description, actions }: PageLayoutProps) {
  const hasHeader = title.trim() !== '' || Boolean(description);

  useEffect(() => {
    const pageTitle = title.trim();
    if (pageTitle) document.title = `${pageTitle} — Queries`;
  }, [title]);

  return (
    <div className="mx-auto max-w-[1440px] px-4 py-6 sm:px-6 md:px-8 md:py-7">
      {hasHeader ? (
        <div className="mb-6 flex flex-col gap-4 border-b border-surface-200 pb-5 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <h1
              className="text-[1.375rem] font-semibold leading-tight tracking-[-0.02em] text-surface-900 focus:outline-none sm:text-2xl"
              data-page-heading
              tabIndex={-1}
            >
              {title}
            </h1>
            {description && (
              <div className="mt-1.5 max-w-3xl text-sm leading-6 text-surface-500">
                {description}
              </div>
            )}
          </div>
          {actions && <div className="flex shrink-0 flex-wrap items-center gap-3">{actions}</div>}
        </div>
      ) : null}

      <main className="focus:outline-none" data-page-main tabIndex={-1}>
        {children}
      </main>
    </div>
  );
}
